import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { AppContext } from '../../App';
import { patchCurrentLab } from '../../clients/lab_client';
import { getLabUsers } from '../../clients/user_client';
import { showToast } from '../../config_and_helpers/helpers';
import { logError, logInfo } from '../../config_and_helpers/logger';
import { excludeLabViewers, isLabAdmin } from '../../utils/userAccountRole';

const LOG_CONTEXT = 'StockOpenDigestRecipientForm';

/**
 * Display label for a lab user option (name + email when present).
 *
 * @param {{ id?: number, first_name?: string, last_name?: string, email?: string }} user
 * @returns {string}
 */
const formatUserOptionLabel = user => {
  const first = String(user?.first_name || '').trim();
  const last = String(user?.last_name || '').trim();
  const name = `${first} ${last}`.trim();
  const email = String(user?.email || '').trim();
  const primary = name || (email ? email : `User ${user?.id}`);
  return email && name ? `${primary} (${email})` : primary;
};

/**
 * Format a notify-user nested object for read-only display.
 *
 * @param {{ first_name?: string, last_name?: string, email?: string }|null|undefined} notifyUser
 * @returns {string}
 */
export const formatNotifyUserDisplay = notifyUser => {
  if (!notifyUser) {
    return 'Not configured';
  }
  const first = String(notifyUser.first_name || '').trim();
  const last = String(notifyUser.last_name || '').trim();
  const name = `${first} ${last}`.trim();
  const email = String(notifyUser.email || '').trim();
  if (name && email) {
    return `${name} — ${email}`;
  }
  return name || email || 'Not configured';
};

/**
 * Admin form + Staff read-only display for the daily stock-open digest recipient.
 * Admins PATCH stock_open_notify_user_id via labs/current/; candidates from GET /users/.
 */
const StockOpenDigestRecipientForm = () => {
  const { token, userDetails, isSupplier, currentLab, setCurrentLab } =
    useContext(AppContext);
  const labAdmin = isLabAdmin(userDetails, isSupplier);

  const savedNotifyUser = currentLab?.stock_open_notify_user ?? null;
  const savedUserId = savedNotifyUser?.id ?? null;

  const [candidates, setCandidates] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(savedUserId);
  const [saving, setSaving] = useState(false);
  const [editDisabled, setEditDisabled] = useState(false);

  useEffect(() => {
    setSelectedUserId(savedUserId);
  }, [savedUserId]);

  const loadCandidates = useCallback(async () => {
    if (!token || !labAdmin) {
      return;
    }
    setLoadingUsers(true);
    const result = await getLabUsers(token);
    setLoadingUsers(false);
    if (!result.success) {
      logError(LOG_CONTEXT, 'getLabUsers failed', {
        status: result.status ?? null,
        error: result.error,
      });
      showToast(
        result.status === 403
          ? 'Not allowed to list lab users.'
          : 'Failed to load lab users.',
        'error',
        'top-right',
        4000
      );
      setCandidates([]);
      return;
    }
    const users = result.users || [];
    const withoutViewers = excludeLabViewers(users);
    const withEmail = withoutViewers.filter(
      user => String(user?.email || '').trim().length > 0
    );
    setCandidates(withEmail);
    logInfo(LOG_CONTEXT, 'digest candidates loaded', {
      count: withEmail.length,
      excludedViewerCount: users.length - withoutViewers.length,
      total: users.length,
    });
  }, [token, labAdmin]);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  const selectedCandidate = useMemo(() => {
    if (selectedUserId == null) {
      return null;
    }
    return (
      candidates.find(u => Number(u.id) === Number(selectedUserId)) || null
    );
  }, [candidates, selectedUserId]);

  const selectedEmail = useMemo(() => {
    if (selectedCandidate?.email) {
      return String(selectedCandidate.email).trim();
    }
    if (
      savedNotifyUser &&
      Number(savedNotifyUser.id) === Number(selectedUserId)
    ) {
      return String(savedNotifyUser.email || '').trim();
    }
    return '';
  }, [selectedCandidate, savedNotifyUser, selectedUserId]);

  const dirty =
    (selectedUserId == null ? null : Number(selectedUserId)) !==
    (savedUserId == null ? null : Number(savedUserId));

  /**
   * @param {number|null} nextUserId
   * @param {'save'|'clear'} action
   */
  const submitNotifyUser = async (nextUserId, action) => {
    if (!token || saving || editDisabled) {
      return;
    }
    setSaving(true);
    logInfo(LOG_CONTEXT, 'digest recipient submit', {
      action,
      stock_open_notify_user_id: nextUserId,
    });
    const result = await patchCurrentLab(token, {
      stock_open_notify_user_id: nextUserId,
    });
    setSaving(false);

    if (result.success) {
      setCurrentLab(result.data);
      setSelectedUserId(result.data?.stock_open_notify_user?.id ?? null);
      showToast(
        action === 'clear'
          ? 'Digest recipient cleared.'
          : 'Digest recipient updated.',
        'success',
        'top-right',
        3000
      );
      logInfo(LOG_CONTEXT, 'digest recipient success', {
        action,
        labId: result.data?.id,
        notifyUserId: result.data?.stock_open_notify_user?.id ?? null,
      });
      return;
    }

    if (result.status === 403) {
      setEditDisabled(true);
      showToast(
        'Only lab Admins can change the digest recipient.',
        'error',
        'top-right',
        4000
      );
      return;
    }

    logError(LOG_CONTEXT, 'digest recipient failed', {
      status: result.status ?? null,
      messages: result.messages,
    });
    showToast(
      result.messages?.[0] || 'Failed to update digest recipient.',
      'error',
      'top-right',
      5000
    );
  };

  if (!labAdmin || editDisabled) {
    return (
      <div className="account-page__row">
        <dt className="account-page__label">Daily stock-open digest recipient</dt>
        <dd className="account-page__value">
          {formatNotifyUserDisplay(savedNotifyUser)}
          <div className="text-muted small mt-1">
            One email per day listing every open (kits appear as multiple rows).
          </div>
        </dd>
      </div>
    );
  }

  return (
    <div className="account-page__row account-page__row--emphasis">
      <Form
        className="stock-open-digest-recipient-form w-100"
        onSubmit={e => {
          e.preventDefault();
          submitNotifyUser(
            selectedUserId == null || selectedUserId === ''
              ? null
              : Number(selectedUserId),
            'save'
          );
        }}
      >
        <Form.Group controlId="stockOpenDigestRecipient" className="mb-2">
          <Form.Label className="account-page__label">
            Daily stock-open digest recipient
          </Form.Label>
          {loadingUsers ? (
            <div className="py-1">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading users…
            </div>
          ) : (
            <Form.Select
              value={selectedUserId == null ? '' : String(selectedUserId)}
              disabled={saving}
              onChange={e => {
                const raw = e.target.value;
                setSelectedUserId(raw ? Number(raw) : null);
              }}
            >
              <option value="">None</option>
              {candidates.map(user => (
                <option key={user.id} value={String(user.id)}>
                  {formatUserOptionLabel(user)}
                </option>
              ))}
            </Form.Select>
          )}
          {selectedEmail ? (
            <Form.Text className="d-block mt-1">{selectedEmail}</Form.Text>
          ) : (
            <Form.Text muted className="d-block mt-1">
              No recipient selected — opens still work; no digest is recorded.
            </Form.Text>
          )}
          <Form.Text muted className="d-block mt-1">
            One email per day listing every open (kits appear as multiple rows).
          </Form.Text>
        </Form.Group>
        <div className="d-flex flex-wrap gap-2">
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={saving || loadingUsers || !dirty}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Saving…
              </>
            ) : (
              'Save recipient'
            )}
          </Button>
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            disabled={saving || loadingUsers || savedUserId == null}
            onClick={() => submitNotifyUser(null, 'clear')}
          >
            Clear
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default StockOpenDigestRecipientForm;
